import { z } from 'zod'
import { type QuickViewSelectItem } from '../../components/menus/quick-view-selector.tsx'
import { type Repository } from '../../utils/api-client/api-schemas.ts'
import { bandForScore, type RiskBandId } from './repository-risk.ts'
import type { RepositoryRiskScore } from './use-repository-risk-scores.ts'

/**
 * The repositories page's quick views, following the findings table's pattern: a named view selects an
 * ordering and a default filter state, rather than swapping in a different table.
 *
 * A Zod enum, matching `scan-quick-view-config.ts` — the in-repo precedent for this same control. The ids
 * were previously written out four times: here, again in the item list, again as a literal comparison in
 * `parseQuickView`, and again as the page's `useQueryState` default. Renaming one compiled fine and
 * silently stopped parsing. Everything now derives from `.options`, and the default has a name.
 */
export const RepositoryQuickViewIdSchema = z.enum(['all-repositories', 'ranked-by-risk'])
export type RepositoryQuickViewId = z.infer<typeof RepositoryQuickViewIdSchema>
export const REPOSITORY_QUICK_VIEWS = RepositoryQuickViewIdSchema.options
export const DEFAULT_REPOSITORY_QUICK_VIEW_ID: RepositoryQuickViewId = 'all-repositories'

const QUICK_VIEW_LABELS: Record<RepositoryQuickViewId, string> = {
  'all-repositories': 'All repositories',
  'ranked-by-risk': 'Ranked by risk',
}

export const REPOSITORY_QUICK_VIEW_ITEMS: readonly QuickViewSelectItem<RepositoryQuickViewId>[] =
  REPOSITORY_QUICK_VIEWS.map(value => ({ label: QUICK_VIEW_LABELS[value], value }))

/**
 * The views on offer. Ranking is only offered once there is an estate to rank — see
 * {@link hasEnoughScoredRepositories}.
 *
 * The selector itself stays either way: it carries the row count and names what you are looking at, so
 * withdrawing the whole control to withdraw one option would cost both.
 */
export const repositoryQuickViewItems = (
  hasRiskData: boolean
): readonly QuickViewSelectItem<RepositoryQuickViewId>[] =>
  hasRiskData
    ? REPOSITORY_QUICK_VIEW_ITEMS
    : REPOSITORY_QUICK_VIEW_ITEMS.filter(item => item.value === DEFAULT_REPOSITORY_QUICK_VIEW_ID)

/** Membership in the enum, so adding or renaming a view cannot leave this behind. */
export const parseQuickView = (raw: string | null): RepositoryQuickViewId =>
  RepositoryQuickViewIdSchema.catch(DEFAULT_REPOSITORY_QUICK_VIEW_ID).parse(raw)

/**
 * A repository whose scorecard produced a usable number. A run that is `processing`, `failed`, or scored
 * no dimension has a state but no score, so nothing that reads a score may treat it as scored.
 *
 * One definition, because the column, the ranking and the band filter all ask this question and a change
 * to the answer has to move them together.
 */
const isScored = (score: RepositoryRiskScore | undefined): score is ScoredRepository => score?.riskScore !== undefined

/** A score narrowed to the case where the number is present, so callers need no second check. */
type ScoredRepository = RepositoryRiskScore & { riskScore: number }

/**
 * Two, not one.
 *
 * Generating a single threat model is usually someone trying the feature to see what it does, and that
 * should not switch on a column, a quick view and a filter across the whole page. Two is the first point
 * at which there is an estate to rank rather than one repository to look at.
 */
const MINIMUM_SCORED_REPOSITORIES = 2

/**
 * Whether the estate has enough scores to be worth ranking. The whole risk UI hangs off this.
 *
 * Counted over the scores on the current page, which is all the client can see. Until the API can order
 * the estate, "enough to rank" is necessarily a statement about what was fetched.
 */
export const hasEnoughScoredRepositories = (scores: Map<string, RepositoryRiskScore>): boolean =>
  [...scores.values()].filter(isScored).length >= MINIMUM_SCORED_REPOSITORIES

/**
 * Orders repositories by the risk their threat model describes, riskiest first.
 *
 * Repositories with no score sort last rather than as zero-risk: an unscored repository is unknown, not
 * safe, and putting it at the safe end would say the opposite.
 *
 * This orders **the rows already fetched**, not the whole estate: the list is paginated server-side and
 * the ordering has to happen before pagination to be a real ranking. ENH-570 moves it to the API — this
 * comparator is the prototype's stand-in until then, not the intended end state.
 */
export const createRiskComparator =
  (scores: Map<string, RepositoryRiskScore>) =>
  (left: Repository, right: Repository): number => {
    const leftScore = scores.get(left.id)?.riskScore
    const rightScore = scores.get(right.id)?.riskScore
    if (leftScore === undefined && rightScore === undefined) {
      return left.full_name.localeCompare(right.full_name)
    }
    if (leftScore === undefined) {
      return 1
    }
    if (rightScore === undefined) {
      return -1
    }
    // Ties break on name so the ranking does not shuffle between refetches.
    return rightScore - leftScore || left.full_name.localeCompare(right.full_name)
  }

/**
 * Keeps only repositories that have a score to be ranked by.
 *
 * The ranked view exists to order an estate by risk, and a repository with no finished threat model has no
 * position in that order — listing it anyway, at whichever end, states something the data does not support.
 * Repositories mid-generation or after a failure are excluded for the same reason: they have a row in the
 * risk rollup but no score in it yet.
 *
 * "Show unranked" in the controls opts them back in for anyone who needs to see what is missing a model.
 */
export const createScoredFilter =
  (scores: Map<string, RepositoryRiskScore>) =>
  (repository: Repository): boolean =>
    isScored(scores.get(repository.id))

/**
 * Keeps only repositories whose score falls in a selected band. An empty selection means no filter at
 * all, matching how the findings filter treats an untouched group.
 */
export const createBandFilter =
  ({ scores, bands }: { scores: Map<string, RepositoryRiskScore>; bands: RiskBandId[] }) =>
  (repository: Repository): boolean => {
    if (bands.length === 0) {
      return true
    }
    const score = scores.get(repository.id)
    if (!isScored(score)) {
      return false
    }
    return bands.includes(bandForScore(score.riskScore).id)
  }
